import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { bookingTypeSelectorAtom } from "@/features/BookingWizard/atoms/selectors/bookingTypeSelectorAtom/bookingTypeSelectorAtom";
import { durationBasedBookingValidGroupSizesSelectorAtom } from "@/features/BookingWizard/atoms/selectors/durationBasedBookingValidGroupSizesSelectorAtom/durationBasedBookingValidGroupSizesSelectorAtom";
import { ListOfBookingWizardStepsAtom } from "@/features/BookingWizard/atoms/stores";

import { setGroupSizeAtom } from "../../../../../../atoms/actions/setGroupSize/setGroupSizeAtom";
import { GroupSizeSelectorAtom } from "../../../../../../atoms/selectors/groupSizeSelectorAtom/groupSizeSelectorAtom";
import { GroupSizeControlBase } from "./GroupSizeControlBase";
import { GroupSizeControlNode$key } from "./__generated__/GroupSizeControlNode.graphql";

type Props = {
  nodeRef: GroupSizeControlNode$key | null;
};

export const GroupSizeControl = ({ nodeRef }: Props) => {
  const node = useFragment(GroupSizeControlFragmentString, nodeRef);
  const currentGroupSize = useAtomValue(GroupSizeSelectorAtom);
  const [wizardSteps, setWizardSteps] = useAtom(ListOfBookingWizardStepsAtom);
  const setGroupSize = useSetAtom(setGroupSizeAtom);
  const bookingType = useAtomValue(bookingTypeSelectorAtom);
  const validDurationGroupSizes = useAtomValue(
    durationBasedBookingValidGroupSizesSelectorAtom
  );
  const maxDurationCapacity = validDurationGroupSizes.reduce(
    (acc, curr) => (curr > acc ? curr : acc),
    0
  );
  const capacity =
    node?.__typename === "TimeSlot"
      ? node?.standardCapacity ?? 4
      : node?.__typename === "AvailabilitySlot"
      ? node?.startSlots?.edges?.[0]?.node?.capacity ?? 4
      : 4;

  const usedCapacity =
    node?.__typename === "TimeSlot"
      ? node?.usedCapacity ?? 0
      : node?.__typename === "AvailabilitySlot"
      ? node?.startSlots?.edges?.[0]?.node?.usedCapacity ?? 0
      : 0;

  useEffect(() => {
    if (currentGroupSize === 1) {
      const filteredSteps = wizardSteps.filter((step) => step !== "AddPlayers");
      if (filteredSteps.length !== wizardSteps.length) {
        setWizardSteps(filteredSteps);
      }
    }
  }, [currentGroupSize, setWizardSteps, wizardSteps]);

  /**
   * For golf we look at the capacity of the current slot
   * for duration we need to check across all valid options in our buckets,
   * so that the user doesn't accidentally land on a start slot with low capacity
   */
  const targetCapacity =
    bookingType === "DurationBooking" ? maxDurationCapacity : capacity;

  return (
    <GroupSizeControlBase
      capacity={targetCapacity}
      usedCapacity={usedCapacity}
      currentGroupSize={currentGroupSize}
      setGroupSize={setGroupSize}
    />
  );
};

export const GroupSizeControlFragmentString = graphql`
  fragment GroupSizeControlNode on Node {
    __typename
    ... on TimeSlot {
      type
      standardCapacity: capacity(bookingType: STANDARD)
      usedCapacity
    }
    ... on AvailabilitySlot {
      id
      startSlots(
        slotAvailability: {
          facilityId: $facilityId
          bookingType: LESSON
          lessonTypeId: $lessonTypeId
        }
      ) {
        edges {
          node {
            ... on TimeSlot {
              id
              capacity(bookingType: LESSON, lessonTypeId: $lessonTypeId)
              usedCapacity
            }
          }
        }
      }
    }
  }
`;
